/**
 * bridge/network_status_controller.js — "network-status" bridge component
 * (Stimulus + Strada).
 *
 * Custom-tier bridge component: reports online/offline + connection type to the
 * web for offline-mode UX, and PUSHES every change. Stimulus identifier
 * "bridge--network-status" (glob-derived from this subdir); Strada component name
 * "network-status" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/NetworkStatusComponent.swift).
 *
 * Unlike the one-shot bridge components (location, clipboard) this is a
 * SUBSCRIPTION: the native side pushes a new status on every connectivity change.
 * To make that pull-free for consumers, this controller publishes the latest
 * status two ways, regardless of platform:
 *   1. document.documentElement.dataset:
 *        data-network-online    = "true" | "false"
 *        data-network-type      = "wifi" | "cellular" | "wired" | "other" | "none"
 *      (CSS / any code can read the current state synchronously).
 *   2. a bubbling document event "chippie:network-status" with
 *        detail: { online: Boolean, connectionType: String }
 *      fired on the INITIAL status and on every change (offline banners,
 *      write-queue flush, "back online" toasts subscribe here).
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection).
 * This controller registers a single networkStatus() impl onto
 * window.ChippieBridge that returns the latest known { online, connectionType }
 * snapshot synchronously:
 *   - ios:     subscribe via this.send("connect", {}, cb) to
 *              NetworkStatusComponent.swift; cb fires on every NWPathMonitor change.
 *   - android: window.ChippieNative.startNetworkStatus(); the native
 *              ConnectivityManager NetworkCallback delivers each change by invoking
 *              the global window.__chippieNetworkStatus(online, connectionType).
 *   - pwa:     navigator.onLine plus window "online"/"offline" events
 *              (connectionType from navigator.connection.type when available, else
 *              "other"/"none").
 *
 * Callers use window.ChippieBridge.networkStatus() for the current snapshot, or
 * (preferred for reactivity) listen for "chippie:network-status" on document.
 *
 * Template wiring (one instance is enough — it's a global subscription; place it
 * in the shell layout):
 *   <div data-controller="bridge--network-status"></div>
 *
 * Outside a native shell BridgeComponent never connect()s, so the PWA path is
 * wired standalone at module load and the controller's connect() upgrades to the
 * native transport inside the iOS shell.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Global name the Android native side calls on every connectivity change. Kept in
// sync with the Kotlin handler (ChippieNativeBridge.startNetworkStatus).
const ANDROID_PUSH_CALLBACK = "__chippieNetworkStatus";

// Latest known status, exposed via the facade's synchronous snapshot getter.
let latest = { online: navigator.onLine, connectionType: "other" };

// Publish a status everywhere: update the <html> dataset flags and fire the
// document event consumers subscribe to. Idempotent values still fire the event
// so a late subscriber re-confirming is harmless; callers debounce if needed.
function publish(online, connectionType) {
    latest = { online: !!online, connectionType: connectionType || (online ? "other" : "none") };
    const root = document.documentElement;
    root.dataset.networkOnline = String(latest.online);
    root.dataset.networkType = latest.connectionType;
    document.dispatchEvent(
        new CustomEvent("chippie:network-status", { detail: { ...latest }, bubbles: true })
    );
}

// PWA transport: navigator.onLine + online/offline events. connectionType is best
// effort from the Network Information API (Chromium WebViews / Android Chrome);
// absent on iOS Safari, so we fall back to a coarse type.
function pwaConnectionType() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!navigator.onLine) return "none";
    if (conn && conn.type) {
        // Network Information API "type" values map closely to ours.
        if (conn.type === "wifi") return "wifi";
        if (conn.type === "cellular") return "cellular";
        if (conn.type === "ethernet") return "wired";
        if (conn.type === "none") return "none";
    }
    return "other";
}

let pwaWired = false;
function startPwa() {
    publish(navigator.onLine, pwaConnectionType());
    if (pwaWired) return;
    pwaWired = true;
    const onChange = () => publish(navigator.onLine, pwaConnectionType());
    window.addEventListener("online", onChange);
    window.addEventListener("offline", onChange);
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.addEventListener) conn.addEventListener("change", onChange);
}

// Register the snapshot getter + standalone (PWA/Android) transports. The facade
// is the SSOT for which transport actually starts; the snapshot getter is the
// same on every platform (it reads `latest`, which each transport keeps current).
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("networkStatus", {
        // ios is overridden by connect() with a real Strada subscription; this
        // placeholder still returns the snapshot if selected before connect.
        ios: () => ({ ...latest }),
        android: () => {
            // Idempotent: starting the native callback twice is harmless, the
            // native side de-dupes. Returns the current snapshot.
            if (window.ChippieNative && window.ChippieNative.startNetworkStatus) {
                if (!window[ANDROID_PUSH_CALLBACK]) {
                    window[ANDROID_PUSH_CALLBACK] = (online, connectionType) =>
                        publish(online, connectionType);
                    try { window.ChippieNative.startNetworkStatus(); } catch (_e) { /* bridge gone */ }
                }
            }
            return { ...latest };
        },
        pwa: () => {
            startPwa();
            return { ...latest };
        },
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

// Start the platform-agnostic PWA listeners immediately so a plain browser /
// PWA has live status without anyone calling the facade. In a native shell these
// are superseded by the richer native push (which also keeps `latest` current).
if (document.readyState !== "loading") {
    startPwa();
} else {
    document.addEventListener("DOMContentLoaded", startPwa, { once: true });
}

export default class extends BridgeComponent {
    static component = "network-status";

    connect() {
        super.connect();
        // Inside the iOS shell now: subscribe to NWPathMonitor pushes and route
        // each update through publish(). Wire the snapshot getter into the facade,
        // keeping the Android/PWA fallbacks intact for non-iOS shells.
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("networkStatus", {
                ios: () => ({ ...latest }),
                android: () => ({ ...latest }),
                pwa: () => ({ ...latest }),
            });
        }
        // One "connect" subscription; the callback re-fires on every native change.
        this.send("connect", {}, (message) => {
            const data = message.data || {};
            publish(data.online, data.connectionType);
        });
    }

    disconnect() {
        // Leaving the iOS shell context: revert the facade to the standalone
        // transports so a snapshot/start still works on the next platform.
        registerStandalone();
        super.disconnect();
    }
}
